import React from 'react';
import { Grid, TextField, InputLabel, Button, InputAdornment, IconButton } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { FormikProps, getIn, FieldArray } from 'formik';
import DeleteRoundedIcon from '@material-ui/icons/DeleteRounded';
import AddCircleIcon from '@material-ui/icons/AddCircleOutlineRounded';
import { AvatarUpload } from '../avatarUpload/avatarUpload';
import { PhoneType } from '../../models/eums/phoneType';
import { EmailType } from '../../models/eums/emailType';
import { useStyles } from './style';
import { getGender, getGenderCode } from '../../utils/personUtils';
import { Gender } from '../../models/eums/gender';
import SelectField from '../selectField/selectField';
import { CountrySelect } from '../countrySelect/countrySelect';
import { DatePicker } from '../datePicker';
import moment from 'moment';
import { PersonModel } from '../../models/personModel';

interface Props extends FormikProps<PersonModel> {
  setAvatar: (file: File) => void;
}

export function PersonProfileCp(props: Props) {
  const {
    values,
    errors,
    handleChange,
    setFieldError,
    setFieldValue,
    setFieldTouched,
    setAvatar,
    touched,
  } = props;
  const { t } = useTranslation();
  const personStyles = useStyles();

  function isFieldError(fieldPath: string) {
    return getIn(touched, fieldPath) && !!getIn(errors, fieldPath);
  }

  function getHelperText(fieldPath: string) {
    if (getIn(touched, fieldPath)) return getIn(errors, fieldPath) || '';

    return '';
  }

  return (
    <Grid container spacing={2}>
      <Grid item className={personStyles.avatarGrid} xs={12}>
        <AvatarUpload
          avatarUrl={values.avatarUrl}
          onChange={image => {
            setAvatar(image);
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} lg={3}>
            <TextField
              required
              error={isFieldError('preferredGivenName')}
              onBlur={() => setFieldTouched('preferredGivenName')}
              variant='outlined'
              margin='normal'
              id='preferredGivenName'
              name='preferredGivenName'
              label={t('first_name')}
              fullWidth
              value={values.preferredGivenName || ''}
              onChange={handleChange('preferredGivenName')}
              helperText={t(getHelperText('preferredGivenName'))}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <TextField
              required
              error={isFieldError('preferredFamilyName')}
              onBlur={() => setFieldTouched('preferredFamilyName')}
              variant='outlined'
              margin='normal'
              id='preferredFamilyName'
              name='preferredFamilyName'
              label={t('last_name')}
              fullWidth
              value={values.preferredFamilyName || ''}
              onChange={handleChange('preferredFamilyName')}
              helperText={t(getHelperText('preferredFamilyName'))}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <TextField
              variant='outlined'
              margin='normal'
              id='nickname'
              name='nickname'
              label={t('nickname')}
              fullWidth
              value={values.nickname || ''}
              onChange={handleChange('nickname')}
            />
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={4} lg={3}>
            <DatePicker
              required
              error={isFieldError('birthDate')}
              onBlur={() => setFieldTouched('birthDate')}
              margin='normal'
              id='date-picker-dialog'
              variant='inline'
              inputVariant='outlined'
              label={t('date_of_birth')}
              format='dd.MM.yyyy.'
              value={values.birthDate || null}
              helperText={t(getHelperText('birthDate'))}
              onChange={(date, val) => {
                if (val) {
                  //Strict parse date to ensure correct user imput
                  const mDate = moment(val, 'DD.MM.YYYY.', true);
                  if (mDate.isValid()) {
                    setFieldValue('birthDate', mDate.format('YYYY-MM-DD'));
                  } else setFieldError('birthDate', 'invalid_date');
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={3} lg={3}>
            <SelectField
              required
              error={isFieldError('genderCode')}
              onBlur={() => setFieldTouched('genderCode')}
              id='genderCode'
              labelId='genderCode'
              name='genderCode'
              inputLabel={t('gender')}
              options={Object.values(Gender).map(g => t(getGender(g)))}
              value={values.genderCode ? t(getGender(values.genderCode)) : t(getGender(Gender.U))}
              onInputChange={value => {
                setFieldValue('genderCode', getGenderCode(value));
              }}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={5} lg={4}>
            <CountrySelect
              required
              error={isFieldError('nationalityCode')}
              helperText={t(getHelperText('nationalityCode'))}
              onBlur={() => setFieldTouched('nationalityCode')}
              id='nationalityCode'
              label={t('nationality')}
              value={values.nationalityCode || ''}
              onChange={nationalityCode => {
                setFieldValue('nationalityCode', nationalityCode);
              }}
            />
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} lg={3}>
            <CountrySelect
              required
              error={isFieldError('residence.country')}
              helperText={t(getHelperText('residence.country'))}
              id='country'
              label={t('country')}
              value={values.residence?.country || ''}
              onBlur={() => setFieldTouched('residence.country')}
              onChange={country => {
                setFieldValue('residence.country', country);
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <TextField
              required
              error={isFieldError('residence.city')}
              onBlur={() => setFieldTouched('residence.city')}
              helperText={t(getHelperText('residence.city'))}
              variant='outlined'
              margin='normal'
              id='city'
              name='city'
              label={t('city')}
              fullWidth
              value={values.residence?.city || ''}
              onChange={handleChange('residence.city')}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={2}>
            <TextField
              required
              error={isFieldError('residence.zip')}
              onBlur={() => setFieldTouched('residence.zip')}
              helperText={t(getHelperText('residence.zip'))}
              variant='outlined'
              margin='normal'
              id='zip'
              name='zip'
              label={t('zip')}
              fullWidth
              value={values.residence?.zip || ''}
              onChange={handleChange('residence.zip')}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <TextField
              required
              error={isFieldError('residence.address')}
              onBlur={() => setFieldTouched('residence.address')}
              helperText={t(getHelperText('residence.address'))}
              variant='outlined'
              margin='normal'
              id='address'
              name='address'
              label={t('address')}
              fullWidth
              value={values.residence?.address || ''}
              onChange={handleChange('residence.address')}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <InputLabel id='social_media'>{t('social_media')}</InputLabel>
      </Grid>
      <Grid item xs={12} sm={4} lg={4}>
        <TextField
          variant='outlined'
          margin='normal'
          id='facebook'
          name='facebook'
          label={t('facebook')}
          fullWidth
          value={values.socialMediaHandles?.facebook || ''}
          onChange={handleChange('socialMediaHandles.facebook')}
        />
      </Grid>
      <Grid item xs={12} sm={4} lg={4}>
        <TextField
          variant='outlined'
          margin='normal'
          id='linkedIn'
          name='linkedIn'
          label={t('linked_in')}
          fullWidth
          value={values.socialMediaHandles?.linkedIn || ''}
          onChange={handleChange('socialMediaHandles.linkedIn')}
        />
      </Grid>
      <Grid item xs={12} sm={4} lg={4}>
        <TextField
          variant='outlined'
          margin='normal'
          id='twitter'
          name='twitter'
          label={t('twitter')}
          fullWidth
          value={values.socialMediaHandles?.twitter || ''}
          onChange={handleChange('socialMediaHandles.twitter')}
        />
      </Grid>
      <Grid item sm={12} lg={6}>
        <Grid container className={personStyles.contactChannelContainer}>
          <Grid item xs={6} sm={6} lg={6}>
            <InputLabel id='email_addresses'>{t('email')}</InputLabel>
          </Grid>
          <Grid item xs={6} sm={6} lg={6} className={personStyles.contactChannelValueGrid}>
            <Button
              size='small'
              color='primary'
              onClick={() => {
                const emailAddresses = [...values.coms.emailAddresses];
                emailAddresses.push({
                  emailType: EmailType.Primary,
                  emailAddress: '',
                });
                setFieldValue('coms.emailAddresses', emailAddresses);
              }}
            >
              <AddCircleIcon />
            </Button>
          </Grid>
        </Grid>
        <FieldArray
          name='coms.emailAddresses'
          render={arrayHelpers => (
            <Grid item xs>
              {values.coms?.emailAddresses && values.coms.emailAddresses.length > 0
                ? values.coms.emailAddresses.map((emailAddress, index) => (
                    <Grid container spacing={1} key={index}>
                      <Grid item xs={12} sm={6} lg={6}>
                        <SelectField
                          id='email_type'
                          inputLabel={t('email_type')}
                          options={Object.values(EmailType).map(et => t(et))}
                          value={t(emailAddress.emailType) || t(EmailType.Primary)}
                          onInputChange={value => {
                            setFieldValue(`coms.emailAddresses.${index}.emailType`, value);
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} lg={6}>
                        <TextField
                          error={isFieldError(`coms.emailAddresses.${index}.emailAddress`)}
                          onBlur={() =>
                            setFieldTouched(`coms.emailAddresses.${index}.emailAddress`)
                          }
                          helperText={t(getHelperText(`coms.emailAddresses.${index}.emailAddress`))}
                          variant='outlined'
                          margin='normal'
                          name='email_address'
                          label={t('email_address')}
                          fullWidth
                          value={emailAddress.emailAddress}
                          onChange={handleChange(`coms.emailAddresses.${index}.emailAddress`)}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position='end'>
                                <IconButton
                                  disabled={values.coms.emailAddresses.length < 2}
                                  size='small'
                                  onClick={() => arrayHelpers.remove(index)}
                                >
                                  <DeleteRoundedIcon />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                  ))
                : ''}
            </Grid>
          )}
        />
      </Grid>
      <Grid item sm={12} lg={6}>
        <Grid container className={personStyles.contactChannelContainer}>
          <Grid item xs={6} sm={6} lg={6}>
            <InputLabel id='phone_numbers'>{t('phone')}</InputLabel>
          </Grid>
          <Grid item xs={6} sm={6} lg={6} className={personStyles.contactChannelValueGrid}>
            <Button
              size='small'
              color='primary'
              onClick={() => {
                const phoneNumbers = [...values.coms.phoneNumbers];
                phoneNumbers.push({
                  phoneType: PhoneType.Mobile,
                  phoneNumber: '',
                });
                setFieldValue('coms.phoneNumbers', phoneNumbers);
              }}
            >
              <AddCircleIcon />
            </Button>
          </Grid>
        </Grid>
        <FieldArray
          name='coms.phoneNumbers'
          render={arrayHelpers => (
            <Grid item xs>
              {values.coms?.phoneNumbers && values.coms.phoneNumbers.length > 0
                ? values.coms.phoneNumbers.map((phoneNumber, index) => (
                    <Grid container spacing={1} key={index}>
                      <Grid item xs={12} sm={6} lg={6}>
                        <SelectField
                          id='phone_type'
                          inputLabel={t('phone_type')}
                          options={Object.values(PhoneType).map(pt => t(pt))}
                          value={t(phoneNumber.phoneType) || t(PhoneType.Mobile)}
                          onInputChange={value => {
                            setFieldValue(`coms.phoneNumbers.${index}.phoneType`, value);
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} lg={6}>
                        <TextField
                          variant='outlined'
                          margin='normal'
                          name='phone_number'
                          label={t('phone_number')}
                          required
                          error={isFieldError(`coms.phoneNumbers.${index}.phoneNumber`)}
                          onBlur={() => setFieldTouched(`coms.phoneNumbers.${index}.phoneNumber`)}
                          helperText={t(getHelperText(`coms.phoneNumbers.${index}.phoneNumber`))}
                          fullWidth
                          value={phoneNumber.phoneNumber}
                          onChange={handleChange(`coms.phoneNumbers.${index}.phoneNumber`)}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position='end'>
                                <IconButton
                                  size='small'
                                  disabled={values.coms.phoneNumbers.length < 2}
                                  onClick={() => arrayHelpers.remove(index)}
                                >
                                  <DeleteRoundedIcon />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                  ))
                : ''}
            </Grid>
          )}
        />
      </Grid>
    </Grid>
  );
}
